import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  DollarSign,
  RefreshCw,
  UserCheck,
  UserX,
  Loader2,
  Shield,
  MessageCircle,
  Bell,
  Mail,
  Store as StoreIcon,
  HelpCircle,
  MessageSquare,
  Send
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OwnerPanelPage() {
  const { user, token, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [supportStats, setSupportStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [openSupportCount, setOpenSupportCount] = useState(0);
  const [trialFunnel, setTrialFunnel] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [expandedTicket, setExpandedTicket] = useState(null);

  // التحقق من أن المستخدم هو المالك (Super Admin)
  const isOwner = isSuperAdmin;

  // جلب عدد الطلبات المعلقة للإشعارات
  const fetchPendingCount = useCallback(async () => {
    if (!isOwner) return;
    try {
      const res = await fetch(`${API_URL}/api/owner/pending-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPendingCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  }, [token, isOwner]);

  const fetchData = useCallback(async () => {
    if (!isOwner) return;
    
    setLoading(true);
    try {
      // جلب الطلبات
      const reqRes = await fetch(`${API_URL}/api/owner/all-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const reqData = await reqRes.json();
      setRequests(reqData.requests || []);
      setStats(reqData.stats);

      // جلب المستخدمين
      const usersRes = await fetch(`${API_URL}/api/owner/all-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = await usersRes.json();
      setUsers(usersData.users || []);

      // جلب طلبات الدعم
      const supportRes = await fetch(`${API_URL}/api/support/all-tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const supportData = await supportRes.json();
      setSupportTickets(supportData.tickets || []);
      setSupportStats(supportData.stats);
      setOpenSupportCount(supportData.stats?.open || 0);

      // جلب إحصائيات قناة التحويل (Trial Funnel)
      try {
        const funnelRes = await fetch(`${API_URL}/api/owner/trial-funnel-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (funnelRes.ok) {
          const funnelData = await funnelRes.json();
          setTrialFunnel(funnelData);
        }
      } catch (e) { /* ignore */ }

      // تحديث عدد الطلبات المعلقة
      fetchPendingCount();

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isOwner, fetchPendingCount]);

  useEffect(() => {
    fetchData();
    
    // تحديث تلقائي كل 30 ثانية
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [fetchData, fetchPendingCount]);

  // معالجة الطلب
  const handleRequest = async (userId, action) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API_URL}/api/owner/approve-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: userId,
          action: action,
          notes: ""
        })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // تفعيل/إلغاء تفعيل مستخدم مباشرة
  const toggleUserActivation = async (email, activate, paid = true) => {
    setActionLoading(email);
    try {
      const endpoint = activate 
        ? `${API_URL}/api/owner/activate-user/${email}?paid=${paid}`
        : `${API_URL}/api/owner/deactivate-user/${email}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // الرد على طلب دعم
  const handleSupportReply = async (ticketNumber) => {
    const reply = replyText[ticketNumber];
    if (!reply?.trim()) return;
    
    setActionLoading(ticketNumber);
    try {
      const response = await fetch(`${API_URL}/api/support/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ticket_id: ticketNumber,
          response: reply
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setReplyText({...replyText, [ticketNumber]: ''});
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // إغلاق طلب دعم
  const handleCloseTicket = async (ticketNumber) => {
    setActionLoading(ticketNumber);
    try {
      const response = await fetch(`${API_URL}/api/support/close/${ticketNumber}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-red-500 max-w-md">
          <CardContent className="p-6 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">صفحة المالك فقط</h2>
            <p className="text-slate-400">هذه الصفحة متاحة فقط لصاحب التطبيق</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-6" dir="rtl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            لوحة المالك
            {pendingCount > 0 && (
              <span className="bg-red-500 text-white text-sm px-2 py-0.5 rounded-full animate-pulse">
                {pendingCount} جديد
              </span>
            )}
          </h1>
          <p className="text-slate-400 text-sm">إدارة طلبات التفعيل والمستخدمين</p>
        </div>
        <Button 
          onClick={fetchData} 
          variant="outline" 
          className="border-slate-600"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
          تحديث
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 text-center">
              <Bell className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.pending}</p>
              <p className="text-sm text-slate-400">طلبات تفعيل</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-4 text-center">
              <HelpCircle className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{openSupportCount}</p>
              <p className="text-sm text-slate-400">طلبات دعم</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.approved_trial}</p>
              <p className="text-sm text-slate-400">فترة تجريبية</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.approved_paid}</p>
              <p className="text-sm text-slate-400">مدفوع</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4 text-center">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.rejected}</p>
              <p className="text-sm text-slate-400">مرفوض</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Info */}
      <Card className="bg-slate-800 border-slate-700 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="text-slate-400 text-sm">سعر التطبيق</p>
                <p className="text-xl font-bold text-white">690,000 د.ع</p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="flex items-center gap-2 bg-emerald-900/30 px-4 py-2 rounded-lg border border-emerald-500/30">
                <MessageCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-white">تواصل مع الدعم الفني للحصول على تفاصيل الدفع</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* قناة التحويل من التجربة → المدفوع */}
      {trialFunnel && (
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-emerald-500/30 mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">📊</span>
              <h2 className="text-lg font-bold text-white">قناة التحويل (Trial Funnel)</h2>
              <span className="text-xs text-slate-400">— صحة منتجك بنظرة واحدة</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <FunnelCard
                label="تجربة نشطة"
                value={trialFunnel.active_trial}
                color="from-emerald-500 to-teal-500"
                icon="🟢"
                testId="funnel-active"
              />
              <FunnelCard
                label="تنتهي خلال 48 ساعة"
                value={trialFunnel.ending_soon}
                color="from-amber-500 to-orange-500"
                icon="⏰"
                testId="funnel-ending-soon"
                pulse={trialFunnel.ending_soon > 0}
              />
              <FunnelCard
                label="منتهية (بدون شراء)"
                value={trialFunnel.expired_trial}
                color="from-red-500 to-rose-500"
                icon="🔴"
                testId="funnel-expired"
              />
              <FunnelCard
                label="مدفوعون نشطون"
                value={trialFunnel.paid}
                color="from-blue-500 to-cyan-500"
                icon="💰"
                testId="funnel-paid"
              />
              <FunnelCard
                label="نسبة التحويل"
                value={`${trialFunnel.conversion_rate}%`}
                color="from-violet-500 to-fuchsia-500"
                icon="📈"
                testId="funnel-conversion-rate"
                highlight
              />
            </div>
            {trialFunnel.expired_trial > trialFunnel.paid * 2 && trialFunnel.paid > 0 && (
              <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200">
                💡 <strong>تنبيه:</strong> عدد المنتهية تجربتهم بدون شراء أعلى من المدفوعين بضعفين — قد يكون السعر أو الميزات تحتاج مراجعة.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <Button 
          variant={activeTab === 'requests' ? 'default' : 'outline'}
          onClick={() => setActiveTab('requests')}
          className={activeTab === 'requests' ? 'bg-emerald-600' : 'border-slate-600'}
        >
          <Bell className="w-4 h-4 me-2" />
          طلبات التفعيل ({pendingRequests.length})
        </Button>
        <Button 
          variant={activeTab === 'support' ? 'default' : 'outline'}
          onClick={() => setActiveTab('support')}
          className={activeTab === 'support' ? 'bg-purple-600' : 'border-slate-600'}
        >
          <HelpCircle className="w-4 h-4 me-2" />
          الدعم الفني ({supportTickets.filter(t => t.status === 'open').length})
        </Button>
        <Button 
          variant={activeTab === 'users' ? 'default' : 'outline'}
          onClick={() => setActiveTab('users')}
          className={activeTab === 'users' ? 'bg-emerald-600' : 'border-slate-600'}
        >
          <Users className="w-4 h-4 me-2" />
          المستخدمين ({users.length})
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="space-y-3">
              {pendingRequests.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">لا توجد طلبات معلقة</p>
                  </CardContent>
                </Card>
              ) : (
                pendingRequests.map((request, index) => (
                  <Card key={index} className="bg-slate-800 border-amber-500/30">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="space-y-2">
                          {/* الاسم */}
                          <p className="text-white font-bold text-lg flex items-center gap-2">
                            <Users className="w-4 h-4 text-emerald-400" />
                            {request.user_name || 'بدون اسم'}
                          </p>
                          
                          {/* الهاتف */}
                          {request.phone && (
                            <p className="text-blue-400 text-sm flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span dir="ltr">{request.phone}</span>
                            </p>
                          )}
                          
                          {/* الإيميل */}
                          <p className="text-slate-400 text-sm flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {request.user_email}
                          </p>
                          
                          {/* اسم المتجر */}
                          {request.store_name && (
                            <p className="text-purple-400 text-sm flex items-center gap-2">
                              <StoreIcon className="w-4 h-4" />
                              {request.store_name}
                            </p>
                          )}
                          
                          {/* التاريخ */}
                          <p className="text-slate-500 text-xs">
                            {new Date(request.created_at).toLocaleString('ar-IQ')}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleRequest(request.user_id, 'approve_trial')}
                            disabled={actionLoading === request.user_id}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {actionLoading === request.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Clock className="w-4 h-4 me-1" />
                                تجربة 5 أيام
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleRequest(request.user_id, 'approve_paid')}
                            disabled={actionLoading === request.user_id}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle className="w-4 h-4 me-1" />
                            تفعيل مدفوع
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRequest(request.user_id, 'reject')}
                            disabled={actionLoading === request.user_id}
                          >
                            <XCircle className="w-4 h-4 me-1" />
                            رفض
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-3">
              {users.map((u, index) => {
                const isActive = u.license?.status === 'active' || u.is_active;
                const isPaid = u.license?.paid_amount >= 690000;
                const isTrial = u.license?.type === 'trial';
                const partnerInfo = u.partner_info || {};
                // التأكد من أن referredBy هو string
                const referredBy = typeof u.referred_by === 'string' ? u.referred_by : (partnerInfo.referral_code || null);
                
                return (
                  <Card 
                    key={index} 
                    className={`bg-slate-800 ${isActive ? 'border-emerald-500/30' : 'border-slate-700'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-wrap justify-between items-center gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                          <div>
                            <p className="text-white font-bold">{u.name || 'بدون اسم'}</p>
                            <p className="text-slate-400 text-sm">{u.email}</p>
                            {u.phone && (
                              <p className="text-blue-400 text-xs" dir="ltr">{u.phone}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-1">
                              {isPaid && (
                                <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">
                                  مدفوع
                                </span>
                              )}
                              {isTrial && (
                                <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded">
                                  تجريبي
                                </span>
                              )}
                              {!isActive && (
                                <span className="bg-slate-500/20 text-slate-400 text-xs px-2 py-0.5 rounded">
                                  غير مفعّل
                                </span>
                              )}
                              {/* عمود الشريك */}
                              {referredBy && (
                                <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                                  partnerInfo?.is_direct_sale 
                                    ? 'bg-purple-500/20 text-purple-400' 
                                    : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  <Users className="w-3 h-3" />
                                  {partnerInfo?.is_direct_sale ? 'مباشر' : partnerInfo?.name || referredBy}
                                  <code className="text-xs opacity-75 ms-1">{referredBy}</code>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!isActive ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => toggleUserActivation(u.email, true, false)}
                                disabled={actionLoading === u.email}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                <Clock className="w-4 h-4 me-1" />
                                تجربة
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => toggleUserActivation(u.email, true, true)}
                                disabled={actionLoading === u.email}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <UserCheck className="w-4 h-4 me-1" />
                                تفعيل
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => toggleUserActivation(u.email, false)}
                              disabled={actionLoading === u.email}
                            >
                              <UserX className="w-4 h-4 me-1" />
                              إلغاء
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <div className="space-y-3">
              {supportTickets.filter(t => t.status !== 'closed').length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">لا توجد طلبات دعم مفتوحة</p>
                  </CardContent>
                </Card>
              ) : (
                supportTickets.filter(t => t.status !== 'closed').map((ticket, index) => (
                  <Card 
                    key={index} 
                    className={`bg-slate-800 ${
                      ticket.status === 'open' ? 'border-purple-500/30' : 'border-blue-500/30'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-slate-500 font-mono">
                                {ticket.ticket_number}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full text-white ${
                                ticket.status === 'open' ? 'bg-amber-500' : 'bg-blue-500'
                              }`}>
                                {ticket.status === 'open' ? 'مفتوح' : 'قيد المعالجة'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full text-white ${
                                ticket.priority === 'high' ? 'bg-red-500' : 
                                ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-500'
                              }`}>
                                {ticket.priority === 'high' ? 'عالية' : 
                                 ticket.priority === 'medium' ? 'متوسطة' : 'منخفضة'}
                              </span>
                            </div>
                            <h3 className="text-white font-bold">{ticket.subject}</h3>
                            <p className="text-slate-400 text-sm">
                              {ticket.user_name} - {ticket.user_email}
                            </p>
                            {ticket.user_phone && (
                              <p className="text-blue-400 text-sm" dir="ltr">{ticket.user_phone}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setExpandedTicket(
                                expandedTicket === ticket.ticket_number ? null : ticket.ticket_number
                              )}
                              className="border-slate-600"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleCloseTicket(ticket.ticket_number)}
                              disabled={actionLoading === ticket.ticket_number}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <CheckCircle className="w-4 h-4 me-1" />
                              إغلاق
                            </Button>
                          </div>
                        </div>

                        {/* الوصف */}
                        <div className="bg-slate-900 rounded-lg p-3">
                          <p className="text-slate-300 text-sm whitespace-pre-wrap">
                            {ticket.description}
                          </p>
                        </div>

                        {/* الردود السابقة */}
                        {ticket.responses && ticket.responses.length > 0 && (
                          <div className="space-y-2">
                            {ticket.responses.map((response, idx) => (
                              <div 
                                key={idx}
                                className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3"
                              >
                                <p className="text-xs text-blue-400 mb-1">
                                  الدعم الفني - {new Date(response.created_at).toLocaleString('ar-IQ')}
                                </p>
                                <p className="text-slate-300 text-sm">{response.message}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* نموذج الرد */}
                        {expandedTicket === ticket.ticket_number && (
                          <div className="space-y-2 pt-2 border-t border-slate-700">
                            <Textarea
                              value={replyText[ticket.ticket_number] || ''}
                              onChange={(e) => setReplyText({
                                ...replyText, 
                                [ticket.ticket_number]: e.target.value
                              })}
                              placeholder="اكتب ردك هنا..."
                              className="bg-slate-900 border-slate-600 text-white min-h-[100px]"
                            />
                            <Button
                              onClick={() => handleSupportReply(ticket.ticket_number)}
                              disabled={actionLoading === ticket.ticket_number || !replyText[ticket.ticket_number]?.trim()}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              {actionLoading === ticket.ticket_number ? (
                                <Loader2 className="w-4 h-4 me-2 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 me-2" />
                              )}
                              إرسال الرد
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FunnelCard({ label, value, color, icon, pulse, highlight, testId }) {
  return (
    <div
      className={`rounded-xl p-3 border ${highlight ? "border-violet-500/40 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10" : "border-slate-700 bg-slate-800/50"} ${pulse ? "animate-pulse" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-base shadow`}>
          {icon}
        </div>
        <p className="text-slate-400 text-xs leading-tight">{label}</p>
      </div>
      <p className="text-white text-2xl font-extrabold">{value}</p>
    </div>
  );
}
